import { useEffect, useState, Fragment } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import {
  ChartBar,
  Users,
  WarningCircle,
  Receipt,
  GlobeHemisphereWest,
  Crown,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  CircleNotch,
  ChartLineUp,
  Path,
  Tag,
  ArrowsClockwise,
  Gauge,
  Stack,
  Lightning,
  Gift,
} from "@phosphor-icons/react";
import AdminPromos from "../components/AdminPromos";

export default function AdminBilling() {
  const { user, booted } = useAuth();
  const [tab, setTab] = useState("users");
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  // Refresh state — `refreshing` drives the spinner on the button,
  // `refreshKey` is bumped on click and threaded to child tabs that
  // own their own fetchers (Analytics, Promos) so they re-pull too.
  const [refreshing, setRefreshing] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);

  const load = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const [overview, subs, failed, byCountry, customers, leads, users] = await Promise.all([
        api.get("/admin/billing/overview"),
        api.get("/admin/billing/subscriptions"),
        api.get("/admin/billing/failed-payments"),
        api.get("/admin/billing/by-country"),
        api.get("/admin/billing/customers"),
        api.get("/admin/billing/contact-leads"),
        api.get("/admin/billing/users"),
      ]);
      setData({
        overview: overview.data,
        subs: subs.data,
        failed: failed.data,
        byCountry: byCountry.data,
        customers: customers.data,
        leads: leads.data,
        users: users.data,
      });
      setLastRefreshedAt(new Date());
    } catch (e) {
      console.error(e);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      // Re-fetch parent data quietly (don't blank the screen) and bump
      // the key so child tabs with their own loaders re-pull as well.
      await load({ silent: true });
      setRefreshKey((k) => k + 1);
      toast.success("Dashboard refreshed.");
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (user && user.role === "admin") load();
  }, [user]);

  if (!booted) return <Loading />;
  if (!user || user === false) return <Navigate to="/login" replace />;
  if (user.role !== "admin") {
    return (
      <div className="py-32 text-center" data-testid="not-admin">
        <XCircle size={36} weight="fill" className="mx-auto text-[var(--accent)] mb-4" />
        <div className="font-display font-bold text-xl">Admin only</div>
        <p className="text-[var(--fg-2)] mt-2">
          You need an admin account to view this page.
        </p>
      </div>
    );
  }

  const ov = data.overview || {};
  return (
    <div className="max-w-[1440px] mx-auto" data-testid="admin-billing-page">
      <div className="border-b border-[var(--border)] px-6 md:px-10 py-10">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="micro-label flex items-center gap-2">
              <ChartBar size={12} weight="bold" /> / Admin · Billing
            </div>
            <h1 className="font-display font-black text-4xl md:text-5xl tracking-tight mt-2">
              Revenue command center
            </h1>
          </div>
          <div className="flex items-center gap-3">
            {lastRefreshedAt && (
              <span
                className="micro-label text-[var(--fg-2)] hidden md:inline"
                data-testid="admin-last-refreshed"
              >
                LAST · {lastRefreshedAt.toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                })}
              </span>
            )}
            <button
              type="button"
              onClick={handleRefresh}
              disabled={refreshing || loading}
              data-testid="admin-refresh-btn"
              className="inline-flex items-center gap-2 px-4 h-10 border border-[var(--border)] bg-white micro-label hover:bg-[var(--bg-2)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Re-pull every tab from the server"
            >
              {refreshing ? (
                <CircleNotch size={14} weight="bold" className="animate-spin" />
              ) : (
                <ArrowsClockwise size={14} weight="bold" />
              )}
              {refreshing ? "REFRESHING…" : "REFRESH"}
            </button>
          </div>
        </div>
      </div>

      {/* Top metrics — pay-per-pack model */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[var(--border)] border-b border-[var(--border)]">
        <Metric label="Registered users" value={(data.users?.length ?? 0).toLocaleString()} icon={<Users size={16} weight="fill" />} />
        <Metric label="Pack revenue 30d" value={`R${(ov.revenue_30d ?? 0).toLocaleString()}`} icon={<Lightning size={16} weight="fill" className="text-[var(--success)]"/>} />
        <Metric label="Packs sold 30d" value={ov.packs_sold_30d ?? ov.payments_30d ?? "—"} />
        <Metric label="Bundle promo" value={ov.bundle_active ? "LIVE" : "Off"} />
      </div>

      {/* Tabs */}
      <div className="border-b border-[var(--border)]">
        <div className="flex overflow-x-auto">
          {[
            { id: "users", label: "Users", icon: Users },
            { id: "promos", label: "Promo codes", icon: Tag },
            { id: "bundle", label: "Bundle promo", icon: Gift },
            { id: "customers", label: "Customers", icon: Users },
            { id: "leads", label: "Contact leads", icon: Receipt },
            { id: "orphaned", label: "Orphaned payments", icon: WarningCircle },
            { id: "analytics", label: "Site traffic", icon: ChartLineUp },
            { id: "converter", label: "Converter health", icon: Gauge },
          ].map((t) => {
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`px-5 h-12 flex items-center gap-2 micro-label border-r border-[var(--border)] transition-colors whitespace-nowrap ${
                  active ? "bg-black text-white" : "hover:bg-[var(--bg-2)]"
                }`}
                data-testid={`admin-tab-${t.id}`}
              >
                <t.icon size={12} weight="bold" /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="p-6 md:p-10">
        {loading ? (
          <Loading />
        ) : (
          <>
            {tab === "users" && <UsersTable rows={data.users || []} onChanged={() => load({ silent: true })} />}
            {tab === "promos" && <AdminPromos refreshKey={refreshKey} />}
            {tab === "bundle" && <BundlePromo refreshKey={refreshKey} />}
            {tab === "customers" && <CustomersTable rows={data.customers || []} />}
            {tab === "leads" && <LeadsTable rows={data.leads || []} onRefresh={() => load({ silent: true })} />}
            {tab === "orphaned" && <OrphanedPaymentsTab refreshKey={refreshKey} />}
            {tab === "analytics" && <AnalyticsTab refreshKey={refreshKey} />}
            {tab === "converter" && <ConverterHealthTab refreshKey={refreshKey} />}
          </>
        )}
      </div>
    </div>
  );
}

function Loading() {
  return (
    <div className="py-20 text-center text-[var(--fg-2)] font-mono text-xs tracking-widest">
      LOADING…
    </div>
  );
}

function Metric({ label, value, warn, icon }) {
  return (
    <div className="bg-white p-5">
      <div className="micro-label flex items-center gap-2">
        {icon}{label}
      </div>
      <div
        className={`font-display font-black text-3xl mt-2 ${
          warn ? "text-[var(--accent)]" : ""
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function SubsTable({ rows, emptyMsg, onChanged }) {
  const [rechecking, setRechecking] = useState(null);
  if (!rows || rows.length === 0) {
    return (
      <div className="hairline bg-white p-10 text-center text-[var(--fg-2)]">
        {emptyMsg || "No subscriptions yet."}
      </div>
    );
  }

  const recheck = async (id) => {
    setRechecking(id);
    try {
      const { data } = await api.post(`/admin/billing/subscriptions/${id}/recheck`);
      if (data.status === "active") {
        toast.success("Payment confirmed by Peach — subscription is now active.");
      } else if (data.status === "failed") {
        toast.error("Peach reports this payment failed.");
      } else {
        toast.info(data.reason || "Peach has not confirmed a payment yet.");
      }
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Re-check failed.");
    } finally {
      setRechecking(null);
    }
  };

  return (
    <div className="hairline bg-white overflow-x-auto" data-testid="subs-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[var(--border)] bg-[var(--bg-2)]">
            <Th>Created</Th>
            <Th>User</Th>
            <Th>Plan</Th>
            <Th>Amount</Th>
            <Th>Status</Th>
            <Th>Gateway</Th>
            <Th>Next renewal</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => (
            <Fragment key={s.id}>
              <tr className="border-b border-[var(--border)] last:border-b-0">
                <Td mono>{s.created_at?.slice(0, 10) || "—"}</Td>
                <Td>{s.user_email || s.user_id}</Td>
                <Td>{s.plan_id}</Td>
                <Td mono>{formatMoney(s.amount, s.currency)}</Td>
                <Td>
                  <StatusChip status={s.status} />
                </Td>
                <Td>{s.provider?.toUpperCase()}</Td>
                <Td mono>{s.next_billing_date?.slice(0, 10) || "—"}</Td>
              </tr>
              {s.status === "pending" && (
                <tr
                  key={`${s.id}-pending`}
                  className="border-b border-[var(--border)] bg-[var(--warning)]/5"
                  data-testid={`pending-reason-${s.id}`}
                >
                  <td colSpan={7} className="px-4 py-3">
                    <div className="flex flex-col md:flex-row md:items-center gap-3">
                      <div className="flex items-start gap-2 flex-1 min-w-0">
                        <Clock size={14} weight="bold" className="text-[var(--warning)] mt-0.5 shrink-0" />
                        <div className="text-[11px] leading-snug text-[var(--fg-2)]">
                          <span className="font-mono uppercase tracking-wider text-[var(--warning)]">
                            Why pending:
                          </span>{" "}
                          {s.pending_reason ||
                            "Checkout opened but payment not completed."}
                          {typeof s.pending_age_minutes === "number" && (
                            <span className="block mt-0.5 font-mono text-[10px] text-[var(--fg-3)]">
                              OPENED {s.pending_age_minutes} MIN AGO
                              {s.pending_likely_abandoned ? " · LIKELY ABANDONED" : ""}
                            </span>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => recheck(s.id)}
                        disabled={rechecking === s.id}
                        className="shrink-0 text-[10px] font-mono uppercase tracking-wider px-3 py-1.5 border border-[var(--border)] hover:bg-black hover:text-white disabled:opacity-50 flex items-center gap-1.5"
                        data-testid={`recheck-${s.id}`}
                      >
                        {rechecking === s.id ? (
                          <CircleNotch size={12} className="animate-spin" />
                        ) : (
                          <ArrowsClockwise size={12} weight="bold" />
                        )}
                        Re-check Peach
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CustomersTable({ rows }) {
  if (!rows || rows.length === 0) {
    return (
      <div className="hairline bg-white p-10 text-center text-[var(--fg-2)]">
        No customers yet.
      </div>
    );
  }
  return (
    <div className="hairline bg-white overflow-x-auto" data-testid="customers-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[var(--border)] bg-[var(--bg-2)]">
            <Th>Joined</Th>
            <Th>Email</Th>
            <Th>Name</Th>
            <Th>Role</Th>
            <Th>Plan</Th>
            <Th>Status</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((u) => (
            <tr key={u.id} className="border-b border-[var(--border)] last:border-b-0">
              <Td mono>{u.created_at?.slice(0, 10) || "—"}</Td>
              <Td>{u.email}</Td>
              <Td>{u.name}</Td>
              <Td>{u.role}</Td>
              <Td>{u.subscription?.plan_id || "free"}</Td>
              <Td>{u.subscription ? <StatusChip status={u.subscription.status} /> : "—"}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function UsersTable({ rows, onChanged }) {
  const [granting, setGranting] = useState(null);
  const [reconciling, setReconciling] = useState(false);
  const [resettingPw, setResettingPw] = useState(null);
  const [resetToken, setResetToken] = useState(null); // { email, token, expires_at }
  if (!rows || rows.length === 0) {
    return (
      <div className="hairline bg-white p-10 text-center text-[var(--fg-2)]">
        No registered users yet.
      </div>
    );
  }

  const grant = async (email) => {
    const amountStr = window.prompt(`Grant conversions to ${email}\n\nHow many conversions? (added as non-expiring paid credits)`);
    if (!amountStr) return;
    const amount = parseInt(amountStr, 10);
    if (!Number.isFinite(amount) || amount === 0) {
      toast.error("Enter a valid non-zero number.");
      return;
    }
    const reason = window.prompt("Reason (gift / refund / support):", "goodwill credit") || "manual_grant";
    setGranting(email);
    try {
      const { data } = await api.post(
        `/admin/billing/users/${encodeURIComponent(email)}/grant-credits`,
        { credits: amount, reason },
      );
      toast.success(`Granted ${amount} conversions to ${email} (now ${data.paid_credits} paid).`);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not grant credits.");
    } finally {
      setGranting(null);
    }
  };

  const resetPassword = async (email) => {
    if (
      !window.confirm(
        `Generate a password reset code for ${email}?\n\nAny previous unused code for this user will be invalidated.`,
      )
    )
      return;
    setResettingPw(email);
    try {
      const { data } = await api.post(
        `/admin/billing/users/${encodeURIComponent(email)}/reset-password-token`,
      );
      setResetToken({ email: data.user_email, token: data.token, expires_at: data.expires_at });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not generate reset code.");
    } finally {
      setResettingPw(null);
    }
  };

  const copyResetToken = async () => {
    try {
      await navigator.clipboard.writeText(resetToken.token);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Could not copy — select the code and copy manually.");
    }
  };

  const reconcile = async () => {
    setReconciling(true);
    try {
      const { data } = await api.post("/admin/billing/reconcile");
      const msg = `Reconcile done — checked ${data.checked}, credited ${data.granted}, still pending ${data.still_pending}, failed ${data.failed}.`;
      if (data.granted > 0) toast.success(msg);
      else toast.info(msg);
      if (data.granted > 0) onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reconcile failed.");
    } finally {
      setReconciling(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="users-tab">
      <div className="flex items-center justify-between gap-3 text-xs font-mono text-[var(--fg-2)] tracking-widest">
        <span>{rows.length} REGISTERED USER{rows.length !== 1 ? "S" : ""}</span>
        <button
          onClick={reconcile}
          disabled={reconciling}
          data-testid="reconcile-payments-btn"
          className="px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider border-2 border-[var(--accent)] bg-[var(--accent)] text-white hover:bg-white hover:text-[var(--accent)] transition-colors disabled:opacity-50"
          title="Find recent paid-but-uncredited pack payments and grant them now"
        >
          {reconciling ? "Reconciling…" : "↻ Reconcile payments"}
        </button>
      </div>
      <p className="text-[11px] text-[var(--fg-2)] leading-relaxed" data-testid="users-legend">
        <strong className="text-[var(--fg)]">Free</strong> = free conversions this month ·{" "}
        <strong className="text-[var(--fg)]">Paid</strong> = purchased/granted (never expire) ·{" "}
        <strong className="text-[var(--accent)]">Balance</strong> = conversions available now (free + paid) ·{" "}
        <strong className="text-[var(--fg)]">Conv. run</strong> = all-time conversions this user has performed.
      </p>
      <div className="hairline bg-white overflow-x-auto" data-testid="users-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--border)] bg-[var(--bg-2)]">
              <Th>Joined</Th>
              <Th>Email</Th>
              <Th>Name</Th>
              <Th>Role</Th>
              <Th>Free</Th>
              <Th>Paid</Th>
              <Th>Balance</Th>
              <Th>Conv. run</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => {
              const free = u.free_credits ?? 0;
              const paid = u.paid_credits ?? 0;
              const isAdmin = u.role === "admin";
              return (
                <tr key={u.id} className="border-b border-[var(--border)] last:border-b-0">
                  <Td mono>{u.created_at?.slice(0, 10) || "—"}</Td>
                  <Td>{u.email}</Td>
                  <Td>{u.name || "—"}</Td>
                  <Td>{u.role}</Td>
                  <Td mono>{isAdmin ? "∞" : free}</Td>
                  <Td mono>{isAdmin ? "∞" : paid}</Td>
                  <Td mono>
                    <span className="font-bold text-[var(--accent)]" title="Conversions available now (free + paid)">
                      {isAdmin ? "∞" : free + paid}
                    </span>
                  </Td>
                  <Td mono>
                    <span className="text-[var(--fg-2)]" title="Total conversions this user has run (all-time history)">
                      {u.conversions_total ?? 0}
                    </span>
                  </Td>
                  <Td>
                    <div className="flex gap-1.5">
                      <button
                        onClick={() => grant(u.email)}
                        disabled={granting === u.email}
                        className="px-3 py-1 font-mono text-[11px] uppercase tracking-wider border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-white transition-colors disabled:opacity-40"
                        data-testid={`grant-credits-${u.email}`}
                      >
                        {granting === u.email ? "…" : "Grant"}
                      </button>
                      <button
                        onClick={() => resetPassword(u.email)}
                        disabled={resettingPw === u.email || isAdmin}
                        title={isAdmin ? "Admin accounts can't be reset from here" : undefined}
                        className="px-3 py-1 font-mono text-[11px] uppercase tracking-wider border border-[var(--fg-2)] text-[var(--fg-2)] hover:bg-[var(--fg)] hover:text-white transition-colors disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-[var(--fg-2)]"
                        data-testid={`reset-password-${u.email}`}
                      >
                        {resettingPw === u.email ? "…" : "Reset PW"}
                      </button>
                    </div>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {resetToken && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          data-testid="reset-token-modal"
        >
          <div className="bg-white max-w-md w-full p-6 hairline">
            <div className="micro-label">/ Password reset code</div>
            <h3 className="font-display font-black text-xl mt-1 break-all">{resetToken.email}</h3>
            <p className="text-xs text-[var(--fg-2)] mt-3 leading-relaxed">
              Send this code to the user through your support conversation (e.g. reply to their
              email). It's single-use and expires on{" "}
              <strong className="text-[var(--fg)]">
                {new Date(resetToken.expires_at).toLocaleString()}
              </strong>
              . Generating a new code for this user invalidates this one.
            </p>
            <div
              className="mt-4 p-3 bg-[var(--bg-2)] border border-[var(--border)] font-mono text-sm break-all select-all"
              data-testid="reset-token-value"
            >
              {resetToken.token}
            </div>
            <div className="flex gap-2 mt-4">
              <button
                onClick={copyResetToken}
                className="btn-primary text-xs px-3 py-2 flex-1"
                data-testid="reset-token-copy-btn"
              >
                Copy code
              </button>
              <button
                onClick={() => setResetToken(null)}
                className="btn-accent text-xs px-3 py-2 flex-1"
                data-testid="reset-token-close-btn"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function LeadsTable({ rows, onRefresh }) {
  const [busy, setBusy] = useState("");
  const [health, setHealth] = useState(null);

  useEffect(() => {
    api.get("/admin/billing/email-health").then((r) => setHealth(r.data)).catch(() => {});
  }, []);

  const setStatus = async (id, status) => {
    setBusy(id);
    try {
      await api.post(`/admin/billing/contact-leads/${id}/status`, { status });
      toast.success(status === "handled" ? "Marked as handled" : "Reopened");
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setBusy("");
    }
  };

  const resend = async (id) => {
    setBusy(id);
    try {
      await api.post(`/admin/billing/contact-leads/${id}/resend`);
      toast.success("Notification re-sent to support");
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="space-y-4" data-testid="leads-panel">
      <div
        className="hairline bg-white p-4 flex flex-wrap items-center gap-x-8 gap-y-2 text-sm"
        data-testid="leads-email-health"
      >
        <div>
          <span className="micro-label block">Email delivery</span>
          <span
            className={`font-mono ${health?.resend_configured ? "text-[var(--success)]" : "text-red-600"}`}
          >
            {health === null
              ? "checking…"
              : health.resend_configured
                ? "Resend connected"
                : "Email off — leads are collected here only"}
          </span>
        </div>
        <div>
          <span className="micro-label block">Support inbox</span>
          <span className="font-mono">{health?.support_email || "—"}</span>
        </div>
        {health?.resend_configured && (
          <>
            <div>
              <span className="micro-label block">Sending from</span>
              <span className="font-mono">{health?.sender_email || "—"}</span>
            </div>
            <div>
              <span className="micro-label block">Failed notifications</span>
              <span className="font-mono">{health?.failed_notifications ?? "—"}</span>
            </div>
          </>
        )}
      </div>

      {!rows || rows.length === 0 ? (
        <div className="hairline bg-white p-10 text-center text-[var(--fg-2)]">
          No contact enquiries yet.
        </div>
      ) : (
        <div className="hairline bg-white overflow-x-auto" data-testid="leads-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--border)] bg-[var(--bg-2)]">
                <Th>Received</Th>
                <Th>Name</Th>
                <Th>Email</Th>
                <Th>Subject</Th>
                <Th>Message</Th>
                <Th>Emailed</Th>
                <Th>Status</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((l) => (
                <tr
                  key={l.id}
                  className="border-b border-[var(--border)] last:border-b-0 align-top"
                  data-testid={`lead-row-${l.id}`}
                >
                  <Td mono>{l.created_at?.slice(0, 16).replace("T", " ") || "—"}</Td>
                  <Td>{l.name}</Td>
                  <Td>
                    <a href={`mailto:${l.email}`} className="underline">
                      {l.email}
                    </a>
                  </Td>
                  <Td>{l.subject || "—"}</Td>
                  <Td>
                    <div className="max-w-sm text-[var(--fg-2)] whitespace-pre-wrap">
                      {l.message || "—"}
                    </div>
                  </Td>
                  <Td mono>
                    {health && !health.resend_configured ? (
                      <span className="text-[var(--fg-2)]">—</span>
                    ) : (
                      <span
                        className={
                          l.notify_sent ? "text-[var(--success)]" : "text-red-600"
                        }
                        title={l.notify_error || ""}
                        data-testid={`lead-emailed-${l.id}`}
                      >
                        {l.notify_sent ? "sent" : "failed"}
                      </span>
                    )}
                  </Td>
                  <Td mono>{l.status || "new"}</Td>
                  <Td>
                    <div className="flex flex-col gap-1">
                      <button
                        disabled={busy === l.id}
                        onClick={() =>
                          setStatus(l.id, l.status === "handled" ? "new" : "handled")
                        }
                        className="btn-primary text-xs px-2 py-1"
                        data-testid={`lead-toggle-${l.id}`}
                      >
                        {l.status === "handled" ? "Reopen" : "Mark handled"}
                      </button>
                      {health?.resend_configured && !l.notify_sent && (
                        <button
                          disabled={busy === l.id}
                          onClick={() => resend(l.id)}
                          className="btn-accent text-xs px-2 py-1"
                          data-testid={`lead-resend-${l.id}`}
                        >
                          Re-send email
                        </button>
                      )}
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ByCountry({ data }) {
  const subs = data.subscriptions_by_currency || [];
  const packs = data.packs_by_currency || [];
  return (
    <div className="grid md:grid-cols-2 gap-8">
      <div className="hairline bg-white" data-testid="by-country-subs">
        <div className="p-4 border-b border-[var(--border)] micro-label">
          Subscription revenue by currency
        </div>
        <MoneyList rows={subs} />
      </div>
      <div className="hairline bg-white" data-testid="by-country-packs">
        <div className="p-4 border-b border-[var(--border)] micro-label">
          Credit pack revenue by currency
        </div>
        <MoneyList rows={packs} />
      </div>
    </div>
  );
}

function MoneyList({ rows }) {
  if (!rows.length) {
    return <div className="p-6 text-center text-[var(--fg-2)]">No data yet.</div>;
  }
  return (
    <ul className="divide-y divide-[var(--border)]">
      {rows.map((r) => (
        <li key={r.currency} className="p-4 flex items-center justify-between">
          <div className="font-mono">{r.currency}</div>
          <div className="text-right">
            <div className="font-display font-bold">
              {formatMoney(r.revenue, r.currency)}
            </div>
            <div className="text-[10px] font-mono text-[var(--fg-2)] tracking-widest">
              {r.count} TXNS
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

function StatusChip({ status }) {
  const tone = {
    active: "ok",
    trial: "ok",
    past_due: "warn",
    pending: "warn",
    canceled: "err",
  }[status];
  return (
    <span className="chip">
      <span className={`status-dot ${tone}`} />
      {(status || "—").toUpperCase()}
    </span>
  );
}

function Th({ children }) {
  return <th className="p-3 text-left micro-label font-normal">{children}</th>;
}
function Td({ children, mono }) {
  return (
    <td className={`p-3 ${mono ? "font-mono text-xs" : ""}`}>{children}</td>
  );
}
function formatMoney(amount, currency) {
  if (amount == null) return "—";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "ZAR",
      maximumFractionDigits: 2,
    }).format(Number(amount));
  } catch {
    return `${currency || ""} ${Number(amount).toFixed(2)}`;
  }
}


const IMPERSONATE_PLANS = [
  { id: "free", label: "Free", desc: "No plan — hits paywall" },
  { id: "standard", label: "Standard", desc: "R59.99 · 20/mo · all modes" },
  { id: "premium", label: "Premium", desc: "R99.99 · 60/mo · social split" },
  { id: "pro", label: "Pro", desc: "R149.99 · 100/mo · advanced + bulk" },
];


function ImpersonationPicker({ currentPlan }) {
  const { refresh } = useAuth();
  const [switching, setSwitching] = useState(null);

  const start = async (planId) => {
    setSwitching(planId);
    try {
      await api.post("/admin/impersonate", { plan: planId });
      await refresh();
      toast.success(
        `Now viewing as ${planId} user. Navigate around to test — click "Return to admin" anytime.`,
      );
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSwitching(null);
    }
  };

  return (
    <div
      className="border-b border-[var(--border)] px-6 md:px-10 py-6 bg-[#fff8f5]"
      data-testid="admin-impersonate-panel"
    >
      <div className="flex items-start gap-3">
        <div className="h-8 w-8 flex items-center justify-center border border-black bg-white mt-1">
          <Eye size={16} weight="bold" />
        </div>
        <div className="flex-1">
          <div className="micro-label">Test as end user</div>
          <h2 className="font-display font-bold text-2xl mt-1">
            Preview every plan from a customer's seat
          </h2>
          <p className="text-[var(--fg-2)] text-sm mt-1 max-w-xl">
            Pick a plan — the whole app treats you as that tier's customer
            (quota, mode gating, bulk access). A floating{" "}
            <strong>Return to admin</strong> button stays visible on every page.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-px bg-[var(--border)] border border-[var(--border)] mt-5">
            {IMPERSONATE_PLANS.map((p) => {
              const active = currentPlan === p.id;
              const isSwitching = switching === p.id;
              return (
                <button
                  key={p.id}
                  onClick={() => start(p.id)}
                  disabled={active || isSwitching}
                  data-testid={`impersonate-${p.id}`}
                  className={`bg-white p-4 text-left transition-colors ${
                    active
                      ? "bg-[var(--accent)] text-white cursor-not-allowed"
                      : "hover:bg-[#fff5f2]"
                  } ${isSwitching ? "opacity-50" : ""}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-display font-bold">{p.label}</span>
                    {isSwitching && (
                      <CircleNotch size={12} className="animate-spin" weight="bold" />
                    )}
                    {active && (
                      <span className="micro-label" style={{ color: "white" }}>
                        ACTIVE
                      </span>
                    )}
                  </div>
                  <div
                    className={`text-xs mt-1 ${
                      active ? "text-white/80" : "text-[var(--fg-2)]"
                    }`}
                  >
                    {p.desc}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}


// ===================== Analytics Tab =====================
function AnalyticsTab({ refreshKey = 0 }) {
  const [window, setWindow] = useState(30);
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);

  const load = async (w) => {
    setLoading(true);
    try {
      const [overview, topPages, byCountry, funnel, engine] = await Promise.all([
        api.get(`/admin/analytics/overview?window=${w}`),
        api.get(`/admin/analytics/top-pages?window=${w}&limit=12`),
        api.get(`/admin/analytics/by-country?window=${w}`),
        api.get(`/admin/analytics/funnel?window=${w}`),
        api.get(`/admin/analytics/engine?window=${w}`),
      ]);
      setData({
        overview: overview.data,
        topPages: topPages.data?.rows || [],
        byCountry: byCountry.data?.rows || [],
        funnel: funnel.data,
        engine: engine.data,
      });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(window);
  }, [window, refreshKey]);

  if (loading) return <Loading />;
  const ov = data.overview || {};
  const en = data.engine || {};
  const fn = data.funnel || {};
  return (
    <div className="space-y-8" data-testid="analytics-tab">
      {/* Window selector */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="micro-label">WINDOW</span>
        {[7, 30, 90].map((w) => (
          <button
            key={w}
            onClick={() => setWindow(w)}
            data-testid={`analytics-window-${w}`}
            className={`px-3 h-8 micro-label border border-[var(--border)] transition-colors ${
              window === w ? "bg-black text-white" : "bg-white hover:bg-[var(--bg-2)]"
            }`}
          >
            {w} DAYS
          </button>
        ))}
      </div>

      {/* Top metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[var(--border)] border border-[var(--border)]">
        <Metric label="Pageviews" value={(ov.pageviews ?? 0).toLocaleString()} />
        <Metric label="Unique visitors" value={(ov.unique_visitors ?? 0).toLocaleString()} />
        <Metric label="Conversions" value={(en.total_conversions ?? 0).toLocaleString()} />
        <Metric
          label="Oversize rate"
          value={`${en.oversize_rate_pct ?? 0}%`}
          warn={en.oversize_rate_pct > 5}
        />
      </div>

      {/* Daily timeseries chart */}
      <div className="bg-white hairline">
        <div className="border-b border-[var(--border)] px-5 py-3 micro-label flex items-center gap-2">
          <ChartLineUp size={12} weight="bold" /> Daily traffic
        </div>
        <div className="p-5">
          <Sparkline series={ov.timeseries || []} window={window} />
        </div>
      </div>

      {/* Funnel */}
      <div className="bg-white hairline">
        <div className="border-b border-[var(--border)] px-5 py-3 micro-label">
          Conversion funnel
        </div>
        <div className="p-5 space-y-3">
          {(fn.steps || []).map((s, i) => {
            const top = fn.steps?.[0]?.count || 1;
            const pct = top ? Math.min(100, (s.count / top) * 100) : 0;
            return (
              <div key={s.key} data-testid={`funnel-${s.key}`}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="font-mono">
                    {String(i + 1).padStart(2, "0")} · {s.label}
                  </span>
                  <span className="font-mono">
                    {s.count.toLocaleString()}
                    {s.pct_of_top != null && (
                      <span className="text-[var(--fg-2)] ml-2">
                        ({s.pct_of_top}%)
                      </span>
                    )}
                  </span>
                </div>
                <div className="h-3 bg-[var(--bg-2)] border border-[var(--border)] relative">
                  <div
                    className="h-full bg-[var(--accent)] transition-all duration-500"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[var(--border)] border border-[var(--border)]">
        {/* Top pages */}
        <div className="bg-white">
          <div className="border-b border-[var(--border)] px-5 py-3 micro-label">
            Top pages
          </div>
          <div className="divide-y divide-[var(--border)]" data-testid="analytics-top-pages">
            {(data.topPages || []).map((r) => (
              <div key={r.path} className="px-5 py-2.5 flex justify-between text-xs font-mono">
                <span className="truncate pr-3">{r.path}</span>
                <span className="text-[var(--fg-2)] tabular-nums">
                  {r.views.toLocaleString()}{" "}
                  <span className="text-[10px]">({r.uniques}u)</span>
                </span>
              </div>
            ))}
            {(data.topPages || []).length === 0 && (
              <div className="px-5 py-4 text-xs text-[var(--fg-2)]">
                No pageviews recorded yet.
              </div>
            )}
          </div>
        </div>

        {/* By country */}
        <div className="bg-white">
          <div className="border-b border-[var(--border)] px-5 py-3 micro-label">
            By country
          </div>
          <div className="divide-y divide-[var(--border)]" data-testid="analytics-by-country">
            {(data.byCountry || []).map((r) => (
              <div key={r.country} className="px-5 py-2.5 flex justify-between text-xs font-mono">
                <span>{r.country}</span>
                <span className="text-[var(--fg-2)] tabular-nums">
                  {r.uniques}u · {r.views}v
                </span>
              </div>
            ))}
            {(data.byCountry || []).length === 0 && (
              <div className="px-5 py-4 text-xs text-[var(--fg-2)]">
                No country data — running locally without CDN headers.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Engine */}
      <div className="bg-white hairline">
        <div className="border-b border-[var(--border)] px-5 py-3 micro-label flex items-center gap-2">
          <Path size={12} weight="bold" /> Conversion engine
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[var(--border)]">
          <Metric label="Avg size" value={`${((en.avg_size_bytes || 0) / 1024).toFixed(1)} KB`} />
          <Metric label="Max size" value={`${((en.max_size_bytes || 0) / 1024).toFixed(1)} KB`} />
          <Metric label="Avg paths" value={(en.avg_paths || 0).toFixed(1)} />
          <Metric label="Anon share" value={`${en.anon_pct ?? 0}%`} />
        </div>
        <div className="p-5">
          <div className="micro-label mb-2">By mode</div>
          <div className="grid grid-cols-3 gap-3" data-testid="analytics-by-mode">
            {(en.by_mode || []).map((m) => (
              <div key={m.mode} className="border border-[var(--border)] p-3">
                <div className="font-mono text-[10px] uppercase text-[var(--fg-2)]">
                  {m.mode}
                </div>
                <div className="font-display text-2xl font-black mt-1">
                  {m.count.toLocaleString()}
                </div>
                <div className="text-[10px] text-[var(--fg-2)] mt-1">
                  avg {((m.avg_size || 0) / 1024).toFixed(1)} KB
                </div>
              </div>
            ))}
            {(en.by_mode || []).length === 0 && (
              <div className="col-span-3 text-xs text-[var(--fg-2)]">
                No conversions in this window yet.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Sparkline({ series, window }) {
  if (!series || series.length === 0) {
    return (
      <div className="text-xs text-[var(--fg-2)] py-12 text-center">
        No data in this window yet — visits start being tracked from now.
      </div>
    );
  }
  // Build a normalized SVG sparkline. Y = pageviews, also draw uniques as a
  // dashed line on the same axis.
  const W = 800;
  const H = 120;
  const PAD = 4;
  const max = Math.max(1, ...series.map((s) => s.pageviews));
  const x = (i) => PAD + (i * (W - 2 * PAD)) / Math.max(1, series.length - 1);
  const y = (v) => H - PAD - (v / max) * (H - 2 * PAD);
  const path = (key) =>
    series
      .map((s, i) => `${i === 0 ? "M" : "L"} ${x(i).toFixed(1)} ${y(s[key]).toFixed(1)}`)
      .join(" ");
  return (
    <div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full h-32"
        data-testid="analytics-sparkline"
      >
        <path d={path("pageviews")} fill="none" stroke="var(--accent)" strokeWidth="2" />
        <path
          d={path("uniques")}
          fill="none"
          stroke="var(--fg)"
          strokeWidth="1.5"
          strokeDasharray="4 4"
        />
      </svg>
      <div className="flex items-center gap-4 text-[10px] font-mono text-[var(--fg-2)] mt-2">
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-0.5 bg-[var(--accent)]" /> Pageviews
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-0.5 bg-[var(--fg)] border-dashed" />{" "}
          Unique visitors
        </span>
        <span className="ml-auto">Last {window} days · {series.length} active</span>
      </div>
    </div>
  );
}

// ===================== Converter Health Tab =====================
const STAGE_LABELS = {
  validate: "Validate",
  normalise: "Normalise",
  ownership_lock: "Ownership lock",
  background_removal: "Background removal",
  trace_optimise: "Trace + optimise",
  limit_check: "Limit check",
};

/* ============================================================ */
/*  Orphaned payments — pending pack purchases stuck >24h        */
/* ============================================================ */
function OrphanedPaymentsTab({ refreshKey = 0 }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState("");
  const [lookupId, setLookupId] = useState("");
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupList, setLookupList] = useState(null);
  const [lookupBusy, setLookupBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/billing/orphaned-payments");
      setRows(data.payments || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load orphaned payments");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const retry = async (id, { onGranted } = {}) => {
    setRetrying(id);
    try {
      const { data } = await api.post(`/admin/billing/orphaned-payments/${id}/retry`);
      if (data.outcome === "granted") {
        toast.success("Confirmed with Peach — credits granted.");
        setRows((r) => r.filter((p) => p.id !== id));
        onGranted?.();
      } else if (data.outcome === "failed") {
        toast.info("Peach confirms this payment failed — marked failed, no credits owed.");
        setRows((r) => r.filter((p) => p.id !== id));
      } else if (data.outcome === "still_pending") {
        toast.info("Peach still reports this as pending — try again shortly.");
      } else {
        toast.error(data.reason ? `Could not retry: ${data.reason}` : "Could not retry.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Retry failed.");
    } finally {
      setRetrying("");
    }
  };

  const lookup = async (e) => {
    e.preventDefault();
    const q = lookupId.trim();
    if (!q) return;
    setLookupBusy(true);
    setLookupResult(null);
    setLookupList(null);
    try {
      if (q.includes("@")) {
        const { data } = await api.get("/admin/billing/payments", { params: { email: q } });
        if (!data.payments || data.payments.length === 0) {
          toast.info("No payments found for that email.");
        }
        setLookupList(data.payments || []);
      } else {
        const { data } = await api.get(`/admin/billing/payments/${encodeURIComponent(q)}`);
        setLookupResult(data);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Payment not found.");
    } finally {
      setLookupBusy(false);
    }
  };

  if (loading && rows.length === 0) return <Loading />;

  return (
    <div className="space-y-4" data-testid="orphaned-payments-tab">
      <div>
        <div className="micro-label flex items-center gap-2">
          <WarningCircle size={12} weight="bold" /> Stuck credit-pack purchases
        </div>
        <p className="text-[var(--fg-2)] text-sm mt-1 max-w-2xl">
          Pack payments still pending &gt;24h with no credits granted — the fallback that should
          have caught these (webhook missed, gateway slow) may have silently failed. Retry
          confirms directly with Peach right now; the background sweep also retries these
          automatically every 2 minutes.
        </p>
      </div>

      <form onSubmit={lookup} className="hairline bg-white p-4 flex flex-wrap items-end gap-3" data-testid="payment-lookup-form">
        <label className="block">
          <span className="micro-label">Look up an order by ID, or all recent orders by email</span>
          <input
            type="text"
            value={lookupId}
            onChange={(e) => setLookupId(e.target.value)}
            placeholder="pack_xxxxxxxxxxxx or user@example.com"
            className="mt-2 w-72 h-10 px-3 border border-[var(--border)] bg-white font-mono text-sm focus:border-black focus:outline-none"
            data-testid="payment-lookup-input"
          />
        </label>
        <button type="submit" disabled={lookupBusy} className="btn-primary text-xs px-4 h-10" data-testid="payment-lookup-submit">
          {lookupBusy ? "…" : "Look up"}
        </button>
      </form>

      {lookupList && lookupList.length > 0 && (
        <div className="hairline bg-white overflow-x-auto" data-testid="payment-lookup-list">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--border)] bg-[var(--bg-2)]">
                <Th>Created</Th>
                <Th>Order ID</Th>
                <Th>Pack</Th>
                <Th>Amount</Th>
                <Th>Status</Th>
                <Th>Granted</Th>
                <Th>Checkout ID</Th>
                <Th>Action</Th>
              </tr>
            </thead>
            <tbody>
              {lookupList.map((p) => (
                <tr key={p.id} className="border-b border-[var(--border)] last:border-b-0" data-testid={`payment-list-row-${p.id}`}>
                  <Td mono>{p.created_at?.slice(0, 16).replace("T", " ") || "—"}</Td>
                  <Td mono>{p.id}</Td>
                  <Td mono>{p.pack_id || "—"}</Td>
                  <Td mono>R{Number(p.amount || 0).toFixed(2)}</Td>
                  <Td mono>{p.status}</Td>
                  <Td mono>{p.credits_granted_at ? "yes" : "no"}</Td>
                  <Td mono>
                    <span className="max-w-[140px] truncate inline-block" title={p.provider_checkout_id || ""}>
                      {p.provider_checkout_id || "—"}
                    </span>
                  </Td>
                  <Td>
                    {!p.credits_granted_at && (
                      <button
                        onClick={() => retry(p.id, { onGranted: () => setLookupList((l) => l.map((x) => (x.id === p.id ? { ...x, credits_granted_at: "pending-refresh" } : x))) })}
                        disabled={retrying === p.id}
                        className="btn-accent text-xs px-3 py-1"
                        data-testid={`payment-list-retry-${p.id}`}
                      >
                        {retrying === p.id ? "…" : "Retry"}
                      </button>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {lookupResult && (
        <div className="hairline bg-white p-5 space-y-3" data-testid="payment-lookup-result">
          <div className="grid sm:grid-cols-2 gap-2 text-sm font-mono">
            <div><span className="text-[var(--fg-2)]">id:</span> {lookupResult.id}</div>
            <div><span className="text-[var(--fg-2)]">user_email:</span> {lookupResult.user_email}</div>
            <div><span className="text-[var(--fg-2)]">status:</span> {lookupResult.status}</div>
            <div><span className="text-[var(--fg-2)]">credits_granted_at:</span> {lookupResult.credits_granted_at || "— not granted —"}</div>
            <div><span className="text-[var(--fg-2)]">provider:</span> {lookupResult.provider}</div>
            <div><span className="text-[var(--fg-2)]">provider_checkout_id:</span> {lookupResult.provider_checkout_id || "—"}</div>
            <div><span className="text-[var(--fg-2)]">amount:</span> R{Number(lookupResult.amount || 0).toFixed(2)}</div>
            <div><span className="text-[var(--fg-2)]">created_at:</span> {lookupResult.created_at}</div>
          </div>
          {!lookupResult.credits_granted_at && (
            <button
              onClick={() => retry(lookupResult.id, { onGranted: () => setLookupResult(null) })}
              disabled={retrying === lookupResult.id}
              className="btn-accent text-xs px-3 py-1.5"
              data-testid="payment-lookup-retry"
            >
              {retrying === lookupResult.id ? "…" : "Retry this order"}
            </button>
          )}
        </div>
      )}

      {rows.length === 0 ? (
        <div className="hairline bg-white p-10 text-center text-[var(--fg-2)]" data-testid="orphaned-payments-empty">
          None — every pack purchase older than 24h has been resolved.
        </div>
      ) : (
        <div className="hairline bg-white overflow-x-auto" data-testid="orphaned-payments-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--border)] bg-[var(--bg-2)]">
                <Th>Created</Th>
                <Th>User</Th>
                <Th>Pack</Th>
                <Th>Credits</Th>
                <Th>Amount</Th>
                <Th>Provider</Th>
                <Th>Checkout ID</Th>
                <Th>Status</Th>
                <Th>Action</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr
                  key={p.id}
                  className="border-b border-[var(--border)] last:border-b-0"
                  data-testid={`orphaned-row-${p.id}`}
                >
                  <Td mono>{p.created_at?.slice(0, 16).replace("T", " ") || "—"}</Td>
                  <Td>{p.user_email}</Td>
                  <Td mono>{p.pack_id || "—"}</Td>
                  <Td mono>{p.credits ?? "—"}</Td>
                  <Td mono>{p.amount != null ? `R${Number(p.amount).toFixed(2)}` : "—"}</Td>
                  <Td mono>{p.provider || "—"}</Td>
                  <Td mono>
                    <span className="max-w-[160px] truncate inline-block" title={p.provider_checkout_id || ""}>
                      {p.provider_checkout_id || "—"}
                    </span>
                  </Td>
                  <Td mono>{p.status || "—"}</Td>
                  <Td>
                    <button
                      onClick={() => retry(p.id)}
                      disabled={retrying === p.id}
                      className="btn-accent text-xs px-3 py-1.5"
                      data-testid={`orphaned-retry-${p.id}`}
                    >
                      {retrying === p.id ? "…" : "Retry"}
                    </button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function ConverterHealthTab({ refreshKey = 0 }) {
  const [m, setM] = useState(null);
  const [loading, setLoading] = useState(true);
  const [auto, setAuto] = useState(true);
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/convert/metrics");
      setM(data);
      setUpdatedAt(new Date());
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load converter metrics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [refreshKey]);

  useEffect(() => {
    if (!auto) return;
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [auto]);

  if (loading && !m) return <Loading />;
  const d = m || {};
  const stageEntries = Object.entries(d.avg_stage_ms || {});
  const maxStage = Math.max(1, ...stageEntries.map(([, v]) => v));
  const capacityPct = d.queue_capacity
    ? Math.min(100, Math.round((d.queue_depth / d.queue_capacity) * 100))
    : 0;
  const nearCapacity = d.queue_depth > 0 && capacityPct >= 60;

  return (
    <div className="space-y-8" data-testid="converter-health-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="micro-label flex items-center gap-2">
            <Lightning size={12} weight="bold" /> Live conversion pipeline
          </div>
          <p className="text-[var(--fg-2)] text-sm mt-1 max-w-xl">
            Worker pool, queue depth and per-stage timings — watch capacity before customers feel it.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {updatedAt && (
            <span className="micro-label text-[var(--fg-2)] hidden md:inline">
              UPDATED · {updatedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
            </span>
          )}
          <button
            onClick={() => setAuto((a) => !a)}
            data-testid="converter-autorefresh-toggle"
            className={`px-3 h-8 micro-label border border-[var(--border)] transition-colors ${
              auto ? "bg-black text-white" : "bg-white hover:bg-[var(--bg-2)]"
            }`}
          >
            {auto ? "LIVE · 5s" : "PAUSED"}
          </button>
        </div>
      </div>

      {nearCapacity && (
        <div
          className="border-2 border-[var(--warning)] bg-[var(--warning)]/5 px-5 py-3 text-sm flex items-center gap-2"
          data-testid="converter-capacity-warning"
        >
          <WarningCircle size={16} weight="fill" className="text-[var(--warning)]" />
          Queue is at {capacityPct}% of capacity ({d.queue_depth}/{d.queue_capacity}). Consider raising
          <code className="mx-1 font-mono">CONVERT_MAX_WORKERS</code> on a larger instance.
        </div>
      )}

      {/* Live queue state */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[var(--border)] border border-[var(--border)]">
        <Metric label="Worker pool" value={d.max_workers ?? "—"} icon={<Stack size={16} weight="fill" />} />
        <Metric label="Active now" value={d.active ?? 0} />
        <Metric label="Queue depth" value={d.queue_depth ?? 0} warn={nearCapacity} />
        <Metric label="Queue capacity" value={d.queue_capacity ?? "—"} />
      </div>

      {/* Throughput */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[var(--border)] border border-[var(--border)]">
        <Metric label="Completed" value={(d.completed ?? 0).toLocaleString()} icon={<CheckCircle size={16} weight="fill" className="text-[var(--success)]" />} />
        <Metric label="Failed" value={d.failed ?? 0} warn={(d.failed ?? 0) > 0} />
        <Metric label="Timeouts" value={d.timeouts ?? 0} warn={(d.timeouts ?? 0) > 0} />
        <Metric label="Timeout rate" value={`${((d.timeout_rate ?? 0) * 100).toFixed(1)}%`} warn={(d.timeout_rate ?? 0) > 0.02} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[var(--border)] border border-[var(--border)]">
        <Metric label="Cache hits" value={(d.cache_hits ?? 0).toLocaleString()} />
        <Metric label="Worker restarts" value={d.worker_restarts ?? 0} warn={(d.worker_restarts ?? 0) > 0} />
        <Metric label="Avg wait" value={`${((d.avg_wait_ms ?? 0) / 1000).toFixed(2)}s`} />
        <Metric label="Avg convert" value={`${((d.avg_convert_ms ?? 0) / 1000).toFixed(2)}s`} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-[var(--border)] border border-[var(--border)]">
        <Metric label="Avg SVG size" value={`${((d.avg_size_bytes ?? 0) / 1024).toFixed(1)} KB`} />
        <Metric label="Avg node count" value={(d.avg_node_count ?? 0).toFixed(0)} />
        <Metric label="Submitted total" value={(d.submitted ?? 0).toLocaleString()} />
      </div>

      {/* Per-stage timings */}
      <div className="bg-white hairline">
        <div className="border-b border-[var(--border)] px-5 py-3 micro-label flex items-center gap-2">
          <Gauge size={12} weight="bold" /> Average per-stage timing
        </div>
        <div className="p-5 space-y-3" data-testid="converter-stage-timings">
          {stageEntries.length === 0 && (
            <div className="text-xs text-[var(--fg-2)]">No conversions recorded yet.</div>
          )}
          {stageEntries.map(([k, v]) => (
            <div key={k} data-testid={`stage-${k}`}>
              <div className="flex justify-between text-xs mb-1">
                <span className="font-mono">{STAGE_LABELS[k] || k}</span>
                <span className="font-mono text-[var(--fg-2)]">{v.toFixed(1)} ms</span>
              </div>
              <div className="h-3 bg-[var(--bg-2)] border border-[var(--border)]">
                <div
                  className="h-full bg-[var(--accent)] transition-all duration-500"
                  style={{ width: `${Math.min(100, (v / maxStage) * 100)}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}



/* ============================================================ */
/*  Bundle promo (buy pack X → Y bonus conversions)             */
/* ============================================================ */
function BundlePromo({ refreshKey }) {
  const [packs, setPacks] = useState([]);
  const [form, setForm] = useState({ active: false, pack_id: "", bonus_credits: 0, label: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [ctx, bundle] = await Promise.all([
          api.get("/pricing/context"),
          api.get("/admin/billing/bundle"),
        ]);
        setPacks(ctx.data.credit_packs || []);
        const b = bundle.data || {};
        setForm({
          active: !!b.active,
          pack_id: b.pack_id || (ctx.data.credit_packs?.[0]?.id ?? ""),
          bonus_credits: b.bonus_credits || 0,
          label: b.label || "",
        });
      } catch {
        toast.error("Could not load bundle settings.");
      } finally {
        setLoading(false);
      }
    })();
  }, [refreshKey]);

  const save = async () => {
    if (form.active && (!form.pack_id || form.bonus_credits <= 0)) {
      toast.error("Pick a pack and a bonus amount greater than 0.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/billing/bundle", form);
      setForm({
        active: !!data.active,
        pack_id: data.pack_id || "",
        bonus_credits: data.bonus_credits || 0,
        label: data.label || "",
      });
      toast.success(data.active ? "Bundle promo is now LIVE." : "Bundle promo turned off.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save bundle.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loading />;

  return (
    <div className="max-w-2xl space-y-6" data-testid="bundle-promo-tab">
      <div>
        <div className="micro-label flex items-center gap-2">
          <Gift size={12} weight="bold" /> / Bundle promo
        </div>
        <h3 className="font-display font-black text-2xl mt-2">Buy a pack → get bonus conversions</h3>
        <p className="text-sm text-[var(--fg-2)] mt-2">
          When active, buying the qualifying pack automatically adds bonus conversions
          (as non-expiring paid credits) on top of the pack.
        </p>
      </div>

      <div className="hairline bg-white p-6 space-y-5">
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={form.active}
            onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
            className="h-5 w-5"
            data-testid="bundle-active-toggle"
          />
          <span className="font-mono text-sm uppercase tracking-wider">
            {form.active ? "Active — LIVE" : "Inactive"}
          </span>
        </label>

        <div>
          <div className="micro-label mb-2">Qualifying pack</div>
          <select
            value={form.pack_id}
            onChange={(e) => setForm((f) => ({ ...f, pack_id: e.target.value }))}
            className="w-full px-3 py-2 border border-[var(--border)] bg-white font-mono text-sm"
            data-testid="bundle-pack-select"
          >
            {packs.map((p) => (
              <option key={p.id} value={p.id}>
                {`${p.name} — ${p.conversions ?? p.credits} conversions (R${p.price_zar})`}
              </option>
            ))}
          </select>
        </div>

        <div>
          <div className="micro-label mb-2">Bonus conversions</div>
          <input
            type="number"
            min={0}
            value={form.bonus_credits}
            onChange={(e) => setForm((f) => ({ ...f, bonus_credits: parseInt(e.target.value, 10) || 0 }))}
            className="w-full px-3 py-2 border border-[var(--border)] bg-white font-mono text-sm"
            data-testid="bundle-bonus-input"
          />
        </div>

        <div>
          <div className="micro-label mb-2">Banner label (optional)</div>
          <input
            type="text"
            value={form.label}
            onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
            placeholder="e.g. Buy Pro, get 5 bonus conversions free!"
            className="w-full px-3 py-2 border border-[var(--border)] bg-white text-sm"
            data-testid="bundle-label-input"
          />
        </div>

        <button
          onClick={save}
          disabled={saving}
          className="btn-accent disabled:opacity-50"
          data-testid="bundle-save-btn"
        >
          {saving ? "Saving…" : "Save bundle"}
        </button>
      </div>
    </div>
  );
}

